/**
 * Settings drawer (spec §14).
 *
 * Two tabs: preferences and diagnostics. The diagnostics tab is the honest
 * half — it names the provider that is actually answering, which model, and
 * what the deployment says its limits are. A translation app that hides its own
 * wiring is impossible to debug and impossible to trust about cost.
 */

import { useState } from "react";
import type { PublicConfig } from "@shared/types/api";
import type { Settings } from "../lib/storage";
import type { TranslationSession } from "../lib/use-session";
import { Badge, Button, Modal, Segmented, Setting, Toggle } from "./ui";
import { IconAlert, IconCheck, IconShield } from "./icons";

interface Props {
  open: boolean;
  onClose: () => void;
  settings: Settings;
  onChange: (patch: Partial<Settings>) => void;
  onReset: () => void;
  session: TranslationSession;
  config: PublicConfig | null;
  browser: { speechRecognition: boolean; speechSynthesis: boolean; audioWorklet: boolean };
}

export function SettingsDialog(props: Props) {
  const { settings, session, config } = props;
  const [tab, setTab] = useState<"preferences" | "diagnostics">("preferences");

  return (
    <Modal
      open={props.open}
      title="Settings"
      onClose={props.onClose}
      wide
      footer={
        <>
          <span className="dim" style={{ fontSize: "var(--step--2)" }}>
            Settings are stored on this device only.
          </span>
          <Button variant="ghost" onClick={props.onReset}>
            Reset to defaults
          </Button>
        </>
      }
    >
      <div style={{ marginBottom: "var(--space-m)" }}>
        <Segmented
          label="Settings section"
          options={[
            { value: "preferences", label: "Preferences" },
            { value: "diagnostics", label: "Diagnostics" },
          ]}
          value={tab}
          onChange={setTab}
        />
      </div>

      {tab === "preferences" ? (
        <div>
          <Setting label="Theme" hint="Subtitle mode renders its own backdrop; this applies to the rest of the app.">
            <Segmented
              label="Theme"
              options={[
                { value: "dark", label: "Dark" },
                { value: "light", label: "Light" },
                { value: "system", label: "System" },
              ]}
              value={settings.theme}
              onChange={(value) => props.onChange({ theme: value })}
            />
          </Setting>

          <Setting
            label="Speak translations"
            hint="Read each finished translation aloud in the target language — speak English, hear Portuguese. Uses the on-device voices below, so it costs nothing."
          >
            <Toggle checked={settings.speakTranslations} onChange={(value) => props.onChange({ speakTranslations: value })} label="Speak translations" />
          </Setting>

          <Setting
            label="Piper neural voices"
            hint="On-device PiperTTS voices (rhasspy/piper) — natural male and female voices, free and offline after a one-time ~60 MB download per voice. Falls back to the browser voice when a model cannot load."
          >
            <Toggle checked={settings.usePiperTts} onChange={(value) => props.onChange({ usePiperTts: value })} label="Use Piper voices" />
          </Setting>

          <Setting label="Piper voice gender" hint="Portuguese and Spanish only have male Piper voices upstream — the picker falls back and says so. English, French, German, Italian and Dutch have both.">
            <Segmented
              label="Piper voice gender"
              options={[
                { value: "female", label: "Female" },
                { value: "male", label: "Male" },
              ]}
              value={settings.piperGender}
              onChange={(value) => props.onChange({ piperGender: value })}
            />
          </Setting>

          <Setting
            label="Custom Piper voice"
            hint="Optional override, e.g. pt_BR-faber-medium. Leave empty to resolve from the target language. Useful when upstream adds new voices."
          >
            <input
              className="input"
              type="text"
              value={settings.piperVoiceId ?? ""}
              placeholder="auto"
              onChange={(event) => props.onChange({ piperVoiceId: event.target.value.trim() === "" ? null : event.target.value.trim() })}
              aria-label="Custom Piper voice id"
              style={{ minWidth: 220 }}
            />
          </Setting>

          <Setting
            label="Browser voices first"
            hint="Prefer on-device speech synthesis over server synthesis. Recommended on the free plan."
          >
            <Toggle checked={settings.useBrowserTts} onChange={(value) => props.onChange({ useBrowserTts: value })} label="Use browser voices" />
          </Setting>

          <Setting
            label="Voices for my languages"
            hint="Downloads the Piper voices for both your source and target language (~60 MB each, once). After that, spoken translations are instant and work offline."
          >
            <Button onClick={() => void session.ensurePiperPair()}>
              Download voices
            </Button>
          </Setting>

          <Setting
            label="On-device transcription"
            hint="Chrome, Edge and Safari can transcribe locally, which costs nothing and feels instant. The server transcript is still authoritative and is what lands in the log."
          >
            <Toggle checked={settings.useBrowserAsr} onChange={(value) => props.onChange({ useBrowserAsr: value })} label="On-device transcription" />
          </Setting>

          <Setting
            label="Live translation of partials"
            hint="Translates the in-progress sentence, not just the finished one. Turn off to roughly halve inference cost."
          >
            <Toggle checked={settings.partialTranslation} onChange={(value) => props.onChange({ partialTranslation: value })} label="Partial translation" />
          </Setting>

          <Setting
            label="Save transcripts"
            hint="Off by default. When on, the text of each finished utterance is written to the deployment's private bucket. Audio is never stored, either way."
          >
            <Toggle checked={settings.persistTranscript} onChange={(value) => props.onChange({ persistTranscript: value })} label="Save transcripts" />
          </Setting>

          <Setting label="Keep translation history" hint="Stores recent text translations in this browser so you can recall them.">
            <Toggle checked={settings.historyEnabled} onChange={(value) => props.onChange({ historyEnabled: value })} label="Keep history" />
          </Setting>

          <Setting label="Show original" hint="Applies to the voice and subtitle stages.">
            <Toggle checked={settings.showOriginal} onChange={(value) => props.onChange({ showOriginal: value })} label="Show original" />
          </Setting>

          <Setting label="Show translation">
            <Toggle checked={settings.showTranslation} onChange={(value) => props.onChange({ showTranslation: value })} label="Show translation" />
          </Setting>
        </div>
      ) : (
        <Diagnostics settings={settings} session={session} config={config} browser={props.browser} />
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------

function Diagnostics({
  settings,
  session,
  config,
  browser,
}: {
  settings: Settings;
  session: TranslationSession;
  config: PublicConfig | null;
  browser: { speechRecognition: boolean; speechSynthesis: boolean; audioWorklet: boolean };
}) {
  return (
    <div className="stack" style={{ gap: "var(--space-m)" }}>
      <section>
        <h3 className="eyebrow" style={{ marginBottom: "var(--space-2xs)" }}>This browser</h3>
        <dl className="dl">
          <dt>Microphone capture</dt>
          <dd>{browser.audioWorklet ? <span style={{ color: "var(--success)" }}>available</span> : <span style={{ color: "var(--danger)" }}>unavailable</span>}</dd>
          <dt>On-device transcription</dt>
          <dd>{browser.speechRecognition ? "available" : "not supported"}</dd>
          <dt>On-device speech</dt>
          <dd>{browser.speechSynthesis ? "available" : "not supported"}</dd>
          <dt>Piper neural voice</dt>
          <dd>{session.piperVoice.voiceId ? `${session.piperVoice.voiceId} (${session.piper.status})` : session.piper.status}</dd>
        </dl>
      </section>

      <section>
        <h3 className="eyebrow" style={{ marginBottom: "var(--space-2xs)" }}>This deployment</h3>
        {config === null ? (
          <div className="badge badge--warn">Could not load the server configuration.</div>
        ) : (
          <>
            <dl className="dl">
              <dt>Mode</dt>
              <dd>{config.deploymentMode}</dd>
              <dt>Translation</dt>
              <dd>{config.capabilities.translation.provider}</dd>
              <dt>Model</dt>
              <dd>{config.capabilities.translation.model ?? "—"}</dd>
              <dt>Context aware</dt>
              <dd>{config.capabilities.translation.context ? "yes" : "via segment priming"}</dd>
              <dt>Speech recognition</dt>
              <dd>{config.capabilities.speech.available ? config.capabilities.speech.provider : "none"}</dd>
              <dt>Language detection</dt>
              <dd>{config.capabilities.detection.available ? config.capabilities.detection.provider : "none"}</dd>
              <dt>Text to speech</dt>
              <dd>{config.capabilities.tts.available ? config.capabilities.tts.provider : "browser only"}</dd>
              <dt>Transcript storage</dt>
              <dd>{config.persistence.storage}</dd>
            </dl>

            <div className="row wrap" style={{ gap: "var(--space-2xs)", marginTop: "var(--space-s)" }}>
              {config.mockAi ? <Badge tone="warn"><IconAlert size={11} /> Mock AI — output is simulated</Badge> : <Badge tone="good"><IconCheck size={11} /> Live models</Badge>}
              <Badge>{config.capabilities.languages.length} languages</Badge>
              <Badge>session ≤ {Math.round((config.limits["maxSessionSeconds"] ?? 0) / 60)} min</Badge>
              <Badge>context {config.limits["maxContextTurns"]} turns</Badge>
              <Badge>segment ≤ {((config.limits["audioSegmentMaxMs"] ?? 0) / 1000).toFixed(0)}s</Badge>
            </div>
          </>
        )}
      </section>

      <section>
        <h3 className="eyebrow" style={{ marginBottom: "var(--space-2xs)" }}>Provider chain</h3>
        {session.providers.length === 0 ? (
          <p className="dim" style={{ fontSize: "var(--step--1)" }}>
            Start a session to see which providers are reachable.
          </p>
        ) : (
          <div className="chip-row">
            {session.providers.map((provider) => (
              <Badge
                key={provider.id}
                tone={provider.mock ? "warn" : provider.available ? "good" : "default"}
                title={provider.detail}
              >
                {provider.id}
                {provider.mock ? " (mock)" : ""}
              </Badge>
            ))}
          </div>
        )}
      </section>

      <section>
        <h3 className="eyebrow" style={{ marginBottom: "var(--space-2xs)" }}>Privacy</h3>
        <div className="inset stack" style={{ padding: "var(--space-s)" }}>
          <div className="row" style={{ gap: "var(--space-2xs)", alignItems: "flex-start" }}>
            <IconShield size={15} style={{ color: "var(--success)", marginTop: 2, flex: "none" }} />
            <div className="stack" style={{ gap: 4 }}>
              <strong style={{ fontSize: "var(--step--1)" }}>Audio is never stored</strong>
              <span className="dim" style={{ fontSize: "var(--step--1)", lineHeight: 1.5 }}>
                Microphone audio exists in memory for the length of one utterance and is discarded as soon as it is transcribed. It is never
                written to durable storage, and no recording survives a session.
              </span>
            </div>
          </div>
          <div className="row" style={{ gap: "var(--space-2xs)", alignItems: "flex-start" }}>
            <IconShield size={15} style={{ color: settings.persistTranscript ? "var(--warning)" : "var(--success)", marginTop: 2, flex: "none" }} />
            <div className="stack" style={{ gap: 4 }}>
              <strong style={{ fontSize: "var(--step--1)" }}>Transcripts: {settings.persistTranscript ? "saving" : "not saving"}</strong>
              <span className="dim" style={{ fontSize: "var(--step--1)", lineHeight: 1.5 }}>
                {settings.persistTranscript
                  ? "Text of each finished utterance is written to the deployment's private bucket so you can retrieve it later."
                  : "Nothing is written. The transcript lives in this tab and disappears when you close it."}
              </span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
